import { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { loadTossPayments, ANONYMOUS } from '@tosspayments/tosspayments-sdk';
import { IoArrowBack } from 'react-icons/io5';
import getApiBaseUrl from './getApiBaseUrl';
import './TossPayment.scss';

/**
 * TossPayment — API 개별 연동 방식 (리다이렉트)
 * props:
 *   amount    - 결제 금액 (number)
 *   orderName - 상품명 (string)
 *   planCode  - 상품 코드 (string, optional)
 *   onClose   - 취소/닫기 콜백
 */
const TossPayment = ({ amount, orderName, planCode, onClose }) => {
const userInfo = useSelector((state) => state.appReducer.userInfo);
const [paying, setPaying] = useState(false);
const [error, setError] = useState('');
const [clientKey, setClientKey] = useState('');
const orderIdRef = useRef(`order-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);

useEffect(() => {
    let mounted = true;

    const loadClientKey = async () => {
    try {
        const serverUrl = getApiBaseUrl();
        const response = await fetch(`${serverUrl}/toss/config`);
        const data = await response.json();

        if (!response.ok) {
        throw new Error(data?.message || '토스 결제 설정을 불러오지 못했습니다.');
        }

        if (mounted && data?.clientKey) {
        setClientKey(data.clientKey);
        }
    } catch (configError) {
        console.error('[TossPayment] config load error:', configError);
        if (mounted) {
        setError(configError?.message || '토스 결제 설정을 불러오지 못했습니다.');
        }
    }
    };

    loadClientKey();

    return () => {
    mounted = false;
    };
}, []);

const handlePay = async () => {
    if (paying) return;
    if (!clientKey) {
    setError('토스 결제 키가 설정되지 않았습니다.');
    return;
    }
    setPaying(true);
    setError('');
    try {
    // customerKey: 영문/숫자/특수문자, 최소 2자 이상 필수
    const customerKey = userInfo?.userId ? `usr_${userInfo.userId}` : ANONYMOUS;
    const tossPayments = await loadTossPayments(clientKey);
    const payment = tossPayments.payment({ customerKey });
    const uid     = userInfo?.userId ? String(userInfo.userId) : '';
    const stCode  = userInfo?.stCode  ? String(userInfo.stCode) : '';
    const pCode   = planCode ? String(planCode) : '';
    const payRequest = {
        method: 'CARD',
        amount: { currency: 'KRW', value: Number(amount) },
        orderId: orderIdRef.current,
        orderName,
        successUrl: `${window.location.origin}/toss/success?userId=${encodeURIComponent(uid)}&stCode=${encodeURIComponent(stCode)}&planCode=${encodeURIComponent(pCode)}`,
        failUrl:    `${window.location.origin}/toss/fail?userId=${encodeURIComponent(uid)}&stCode=${encodeURIComponent(stCode)}`,
        card: {
        useEscrow: false,
        flowMode: 'DEFAULT',
        useCardPoint: false,
        useAppCardOnly: false,
        },
    };
    if (userInfo?.email)    payRequest.customerEmail = userInfo.email;
    if (userInfo?.userName)  payRequest.customerName  = userInfo.userName;
    console.log('[TossPayment] requestPayment payload:', payRequest);
    await payment.requestPayment(payRequest);
    } catch (e) {
    console.error('[TossPayment] 결제 에러:', e);
    const msg = e?.message || (typeof e === 'string' ? e : JSON.stringify(e)) || '결제 요청 실패';
    setError(msg);
    setPaying(false);
    }
};

return (
    <div className="toss-payment-wrap">
    <div className="toss-header">
        <h3 className="toss-title">{orderName}</h3>
        <span className="toss-amount">{Number(amount).toLocaleString()}원</span>
        {onClose && (
        <button className="toss-close-btn" onClick={onClose} aria-label="닫기">✕</button>
        )}
    </div>

    {error && <p className="toss-error">{error}</p>}

    <p className="toss-notice">결제하기를 누르면 토스페이먼츠 결제 페이지로 이동합니다.</p>

    <button
        className="toss-pay-btn"
        onClick={handlePay}
        disabled={paying}
    >
        {paying ? '이동 중...' : `${Number(amount).toLocaleString()}원 결제하기`}
    </button>

    {onClose && (
        <button className="toss-back-btn" onClick={onClose} disabled={paying}>
        <IoArrowBack style={{ verticalAlign: 'middle', marginRight: '4px' }} /> <span style={{ verticalAlign: 'middle' }}>다른 금액 선택</span>
        </button>
    )}
    </div>
);
};

export default TossPayment;
